import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Json } from '@/integrations/supabase/types';
import { useSupabaseAuth } from '@/hooks/auth/supabaseAuth';
import { getAllCoursesUnified } from '@/utils/allCourses';
import { useAutoMigration } from '@/hooks/useAutoMigration';
import { toast } from 'sonner';
import { generateCourseRoute } from '@/utils/routeUtils';

export interface Course {
  id: string;
  title: string;
  description: string;
  category: string;
  icon?: string;
  route: string;
  level: string;
  duration_hours: number;
  total_modules: number;
  is_active: boolean;
  course_status?: 'active' | 'coming_soon' | 'archived' | 'draft';
  prerequisites: string[];
  learning_outcomes: string[];
  metadata: Json;
  created_at: string;
  updated_at: string;
  // Additional properties for course details
  modules?: any[];
  modules_content?: any[];
  tags?: string[];
  certification_info?: {
    name?: string;
    code?: string;
    exam_name?: string;
    exam_code?: string;
    exam_duration?: string;
    passing_score?: string;
    preparation_time?: string;
  };
  learningOutcomes?: string[];
  image?: string;
  totalDuration?: string;
}

export interface CourseCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  sort_order: number;
  created_at: string;
}

export interface CourseWithCategory extends Course {
  categoryName: string;
  categoryIcon: string;
  categoryColor: string;
}

export const useCourses = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [categories, setCategories] = useState<CourseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { hasAccess } = useSupabaseAuth();
  const autoMigration = useAutoMigration();

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch courses and categories in parallel
      const [coursesResponse, categoriesResponse] = await Promise.all([
        supabase
          .from('courses')
          .select('*')
          .eq('is_active', true)
          .order('title'),
        supabase
          .from('course_categories')
          .select('*')
          .order('sort_order')
      ]);

      if (categoriesResponse.error) {
        throw categoriesResponse.error;
      }

      // Handle courses - trigger auto-migration if database is empty/insufficient
      if (coursesResponse.error || !coursesResponse.data || coursesResponse.data.length === 0) {
        console.warn('Database courses empty or failed, using static fallback');
        
        // Transform unified courses to match database format
        const staticCourses = getAllCoursesUnified();
        const transformedCourses = staticCourses.map(course => ({
          id: course.id,
          title: course.title,
          description: course.description,
          category: course.courseType || 'react',
          icon: null,
          route: generateCourseRoute(course.id),
          level: course.level || 'beginner',
          duration_hours: parseInt(course.duration?.match(/\d+/)?.[0] || course.totalDuration?.match(/\d+/)?.[0] || '2'),
          total_modules: course.modules?.length || 0,
          is_active: true,
          prerequisites: course.prerequisites || [],
          learning_outcomes: course.learningOutcomes || course.objectives || [],
          metadata: { source: 'static_fallback', courseType: course.courseType },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));
        
        setCourses(transformedCourses);
        
        // DISABLED: Auto-migration disabled to protect enhanced content from being overwritten
        // if (!autoMigration.isRunning && !autoMigration.hasRun) {
        //   autoMigration.triggerMigrationIfNeeded();
        // }
      } else {
        setCourses(coursesResponse.data.map(course => ({
          ...course,
          course_status: (course.course_status || 'active') as 'active' | 'coming_soon' | 'archived' | 'draft'
        })));
      }

      setCategories(categoriesResponse.data || []);
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch courses');
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  // Get courses with category information
  const getCoursesWithCategories = (): CourseWithCategory[] => {
    return courses.map(course => {
      const category = categories.find(cat => cat.id === course.category);
      return {
        ...course,
        categoryName: category?.name || course.category,
        categoryIcon: category?.icon || 'Code',
        categoryColor: category?.color || 'blue'
      };
    });
  };

  // Get courses filtered by category
  const getCoursesByCategory = (categoryId: string): CourseWithCategory[] => {
    const coursesWithCategories = getCoursesWithCategories();
    if (categoryId === 'all') {
      return coursesWithCategories;
    }
    return coursesWithCategories.filter(course => course.category === categoryId);
  };

  // Get course statistics
  const getCourseStats = () => {
    const totalCourses = courses.length;
    const totalEnrollments = courses.reduce((sum, course) => {
      const metadata = course.metadata as any;
      return sum + (metadata?.enrollment_count || 0);
    }, 0);
    const avgCompletionRate = courses.length > 0 
      ? courses.reduce((sum, course) => {
          const metadata = course.metadata as any;
          return sum + (metadata?.completion_rate || 0);
        }, 0) / courses.length
      : 0;

    return {
      totalCourses,
      totalEnrollments,
      avgCompletionRate: Math.round(avgCompletionRate)
    };
  };

  return {
    courses,
    categories,
    loading,
    error,
    getCoursesWithCategories,
    getCoursesByCategory,
    getCourseStats,
    refetch: fetchCourses,
    autoMigration
  };
};